import { useCallback, useEffect, useState } from 'react';
import { hapticFeedback, hapticNotification } from '../lib/telegram';

const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV
    ? 'http://localhost:8787'
    : 'https://teleclaw-api.teleclaw.workers.dev');

interface RegistrationRequest {
  id: string;
  bot_username: string;
  name: string;
  description: string | null;
  category: string;
  website: string | null;
  tags: string | null;
  logo_url: string | null;
  status: string;
  expires_at: string;
}

interface Props {
  requestId: string;
  onBack: () => void;
}

export function ConfirmScreen({ requestId, onBack }: Props) {
  const [request, setRequest] = useState<RegistrationRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<{ success: boolean; agent_id?: string; view_url?: string; error?: string } | null>(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/register-request/${requestId}`)
      .then(r => r.json())
      .then((data: any) => {
        if (data.error) setError(data.error);
        else setRequest(data);
      })
      .catch(() => setError('Failed to load request'))
      .finally(() => setLoading(false));
  }, [requestId]);

  const handleConfirm = useCallback(async () => {
    if (!request) return;
    setConfirming(true);
    hapticFeedback('medium');

    try {
      // Get user ID from Telegram
      const userId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
      if (!userId) {
        setResult({ success: false, error: 'Must be opened in Telegram to confirm' });
        return;
      }

      const resp = await fetch(`${API_BASE_URL}/api/register-request/${requestId}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telegram_user_id: userId.toString() }),
      });
      const data = await resp.json() as any;
      setResult(data);
      if (data.success) hapticNotification('success');
      else hapticNotification('error');
    } catch {
      setResult({ success: false, error: 'Network error' });
      hapticNotification('error');
    } finally {
      setConfirming(false);
    }
  }, [request, requestId]);

  const handleReject = useCallback(async () => {
    hapticFeedback('medium');
    await fetch(`${API_BASE_URL}/api/register-request/${requestId}/reject`, { method: 'POST' });
    hapticNotification('warning');
    setResult({ success: false, error: 'Registration rejected' });
  }, [requestId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-4xl mb-4">❌</p>
        <p className="text-red-400">{error}</p>
        <button onClick={onBack} className="mt-4 px-4 py-2 bg-blue-600 rounded-lg">Go Home</button>
      </div>
    );
  }

  if (result) {
    return (
      <div className="text-center py-12">
        {result.success ? (
          <>
            <p className="text-4xl mb-4">✅</p>
            <h2 className="text-xl font-bold mb-2">App Registered!</h2>
            <p className="text-[var(--tg-theme-hint-color)] mb-4">{request?.name} has been listed on TeleClaw.</p>
            {result.view_url && (
              <a href={result.view_url} className="inline-block px-6 py-3 bg-blue-600 rounded-xl font-medium">
                View Listing
              </a>
            )}
          </>
        ) : (
          <>
            <p className="text-4xl mb-4">❌</p>
            <p className="text-red-400 mb-4">{result.error}</p>
            <button onClick={onBack} className="px-4 py-2 bg-blue-600 rounded-lg">Go Home</button>
          </>
        )}
      </div>
    );
  }

  if (!request) return null;

  const tags = request.tags ? JSON.parse(request.tags) : [];
  const isExpired = new Date(request.expires_at) < new Date();

  if (request.status !== 'pending' || isExpired) {
    return (
      <div className="text-center py-12">
        <p className="text-4xl mb-4">ℹ️</p>
        <p className="text-[var(--tg-theme-hint-color)]">
          This request has {isExpired ? 'expired' : `been ${request.status}`}.
        </p>
        <button onClick={onBack} className="mt-4 px-4 py-2 bg-blue-600 rounded-lg">Go Home</button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-6">
      <div className="text-center mb-6">
        <p className="text-4xl mb-2">🦞</p>
        <h2 className="text-xl font-bold">Confirm App Registration</h2>
        <p className="text-sm text-[var(--tg-theme-hint-color)]">An app wants to be listed on TeleClaw</p>
      </div>

      <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-4 space-y-3 mb-6">
        <div>
          <span className="text-xs text-[var(--tg-theme-hint-color)]">Name</span>
          <p className="font-medium">{request.name}</p>
        </div>
        <div>
          <span className="text-xs text-[var(--tg-theme-hint-color)]">Bot</span>
          <p className="font-medium">@{request.bot_username}</p>
        </div>
        <div>
          <span className="text-xs text-[var(--tg-theme-hint-color)]">Category</span>
          <p className="font-medium capitalize">{request.category}</p>
        </div>
        {request.description && (
          <div>
            <span className="text-xs text-[var(--tg-theme-hint-color)]">Description</span>
            <p className="text-sm">{request.description}</p>
          </div>
        )}
        {request.website && (
          <div>
            <span className="text-xs text-[var(--tg-theme-hint-color)]">Website</span>
            <p className="text-sm">{request.website}</p>
          </div>
        )}
        {tags.length > 0 && (
          <div>
            <span className="text-xs text-[var(--tg-theme-hint-color)]">Tags</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {tags.map((t: string) => (
                <span key={t} className="px-2 py-0.5 bg-blue-600/20 text-blue-400 rounded text-xs">{t}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleReject}
          className="flex-1 py-3 bg-red-600/20 text-red-400 rounded-xl font-medium hover:bg-red-600/30 transition-colors"
        >
          ❌ Reject
        </button>
        <button
          onClick={handleConfirm}
          disabled={confirming}
          className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
        >
          {confirming ? '...' : '✅ Confirm'}
        </button>
      </div>
    </div>
  );
}
